import { useMemo, useState, type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useQueryClient } from '@mint-lib/query-client';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@mint-lib/tw-components';
import { ApprovalFlows } from '@mint-sdk/api-approval-flows';
import { getRouteApi } from '@mint/routing';

import useUpdateFlowMutation from '../api/mutations/useUpdateFlowMutation.js';
import { useSetDeleteFlowModalContext } from '../atoms/deleteFlowModal.js';
import { APPROVAl_FLOW_FORM_ID } from '../constants/singleFlow.js';
import { getTabsList } from '../constants/tabs.js';
import EditApprovalHeader from '../features/FlowForm/EditApprovalHeader.jsx';
import {
  FlowFormInput,
  FlowFormOutput,
  getFlowFormSchema,
} from '../types/singleFlow.js';
import { getFormValuesFromFlowData } from '../utils/singleFlow.js';
import {
  mapEmails,
  mapRequestors,
  mergeApproversWithId,
  normalizeFlowItem,
  startOfDayUTC,
  endOfDayUTC,
} from '../utils/transformers';
import { LeavePageModal } from './modals/LeavePageModal.jsx';

type Props = {
  flowData: ApprovalFlows.FlowItem;
};

const routeApi = getRouteApi('/_secured/company-profile/flows/$flowId/');

const FlowPageContainer: FC<Props> = ({ flowData }) => {
  const { t } = useTranslation('@myn/approval_v2');
  const navigate = routeApi.useNavigate();

  const queryClient = useQueryClient();
  const { mutateAsync: updateFlow } = useUpdateFlowMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const normalizedFlowItem = useMemo(() => {
    return normalizeFlowItem(flowData);
  }, [flowData]);
  const formMethods = useForm<FlowFormInput, unknown, FlowFormOutput>({
    mode: 'onChange',
    resolver: zodResolver(getFlowFormSchema(t)),
    values: getFormValuesFromFlowData(normalizedFlowItem),
  });
  const setDeleteFlowModalContext = useSetDeleteFlowModalContext();

  const {
    handleSubmit,
    formState: { isDirty, dirtyFields },
  } = formMethods;

  const tabsList = getTabsList(t);

  const shouldBlockFn = () => {
    return isDirty;
  };

  const handleFormSubmit = handleSubmit(async (formData) => {
    setIsSubmitting(true);

    const approverItemIds = flowData.approval.reduce<string[]>(
      (acc, approvalItem) => {
        if (approvalItem.type === 'operation') {
          return acc;
        }

        acc.push(approvalItem.id);
        return acc;
      },
      [],
    );
    const endDate = formData.dateTo ? endOfDayUTC(formData.dateTo) : null;

    try {
      await updateFlow({
        urlParams: {
          flowId: normalizedFlowItem.id,
        },
        data: {
          id: normalizedFlowItem.id,
          flowName: dirtyFields.name ? formData.name : normalizedFlowItem.name,
          requestor: {
            id: flowData.requestGroup.id,
            ...mapRequestors(formData.requesterType, formData.requesterValue),
          },
          approvers: mergeApproversWithId(formData.approvers, approverItemIds),
          notifications: dirtyFields.pendingRequestNotification
            ? {
                enabled: formData.pendingRequestNotification.enabled,
                notifyIn: formData.pendingRequestNotification.frequencyDays,
              }
            : {
                enabled: normalizedFlowItem.pendingRequestNotification.enabled,
                notifyIn:
                  normalizedFlowItem.pendingRequestNotification.frequencyDays,
              },
          notificationPeriod: formData.pendingRequestNotification.enabled
            ? formData.pendingRequestNotification.frequencyDays
            : null,
          startDate: dirtyFields.dateFrom
            ? startOfDayUTC(formData.dateFrom)
            : normalizedFlowItem.dateFrom,
          endDate: dirtyFields.dateTo ? endDate : normalizedFlowItem.dateTo,
          isEmailsForApprovedEntityEnabled: dirtyFields
            .approvedRequestNotification?.enabled
            ? formData.approvedRequestNotification.enabled
            : normalizedFlowItem.approvedRequestNotification.enabled,
          emailsForApprovedEntity: dirtyFields.approvedRequestNotification
            ?.recipientEmails
            ? mapEmails(formData.approvedRequestNotification.recipientEmails)
            : normalizedFlowItem.approvedRequestNotification.recipientEmails,
        },
      });
      ApprovalFlows.listFlows.invalidateAll(queryClient, {});
      await ApprovalFlows.getFlow.invalidate(queryClient, {
        urlParams: {
          flowId: normalizedFlowItem.id,
        },
      });

      enqueueSnackbar({
        variant: 'success',
        title: t('Flow updated.'),
      });
    } catch {
      enqueueSnackbar({
        variant: 'error',
        title: t('Flow was not updated.'),
      });
    } finally {
      setIsSubmitting(false);
    }
  });

  const handleDelete = () => {
    setDeleteFlowModalContext({
      isOpened: true,
      flowId: flowData.id,
      inUse: flowData.inUse,
      onSuccess: () => {
        formMethods.reset();
        setTimeout(() => {
          navigate({
            to: '/company-profile/flows',
          });
        });
      },
    });
  };

  return (
    <FormProvider {...formMethods}>
      <form
        className="relative"
        onSubmit={handleFormSubmit}
        id={APPROVAl_FLOW_FORM_ID}
      >
        <div className="grid gap-4">
          <EditApprovalHeader
            flowName={normalizedFlowItem.name}
            lastEditedAt={normalizedFlowItem.lastEditedAt}
            isFlowInUse={normalizedFlowItem.inUse}
            isPending={isSubmitting}
            onDelete={handleDelete}
          />
          <div>
            <Tabs defaultValue={tabsList[0].id} orientation="horizontal">
              <TabsList className="w-full">
                {tabsList.map(({ id, label, a11yLabel }) => {
                  return (
                    <TabsTrigger
                      key={id}
                      value={id}
                      data-dd-action-name={a11yLabel}
                    >
                      {label}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
              <div className="mt-6">
                {tabsList.map(({ id, component: Component }) => {
                  return (
                    <TabsContent key={id} value={id}>
                      {<Component isFlowInUse={flowData.inUse} />}
                    </TabsContent>
                  );
                })}
              </div>
            </Tabs>
          </div>
        </div>
      </form>
      <LeavePageModal shouldBlockFn={shouldBlockFn} />
    </FormProvider>
  );
};

export default FlowPageContainer;
